/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */
import i18next from 'i18next';
import i18nextHttpBackend from 'i18next-http-backend';
import { iamLogin, LocalizationParameters } from './main';
import { iam } from './iam';

export const iamLocalization = {
  maxInt: 2147483647, // 32 bit signed integer max value
  
  // This map will contain all keys that have not been localized yet by the L10N team. Add any new keys here.
  // After localization is complete, and we have testing confirmation from the L10N team, delete key from list
  // and alter code that uses it if necessary. Keys can have a value type of string (a direct translation),
  // function (returns the translation), or boolean (simply a flag for turning on or off functionality).
  // Examples:
  // const stringMap = {
  //   dashboard: 'McAfee Dashboard',
  //   logoutMessage: arg => {
  //     return 'Successfully logged out of McAfee MVISION';
  //   },
  //   changePasswordTooltipStrings: true
  // };
  stringMap: { // eslint-disable-line prefer-const
  } as { [key: string]: string | ((parameters?: LocalizationParameters) => string) | boolean },
  
  /*
  @param key - L10N key of string
  @param args - Default value to be used
  @param parameters - parameters for i18next.t
   */
  localize: (key: string, parameters?: LocalizationParameters): string => { // eslint-disable-line no-unused-vars
    if (iam.settings.testNewStrings || !Object.prototype.hasOwnProperty.call(iamLocalization.stringMap, key)) return i18next.t(key, parameters);
    const value = iamLocalization.stringMap[key];
    switch (typeof value) {
      case 'string': return value;
      case 'function': return value(parameters);
      default: return key;
    }
  },
  
  /*
  @param key - L10N key of string
   */
  isTranslationPending: (key: string) => { // eslint-disable-line no-unused-vars
    return !iam.settings.testNewStrings && Object.prototype.hasOwnProperty.call(iamLocalization.stringMap, key);
  },
  
  randomInt: () => {
    // return random number between 1 and maxInt
    return Math.floor(Math.random() * iamLocalization.maxInt) + 1;
  },
  
  loadI18NLibs: (callback: () => void) => {
    const userLang = iamLocalization.getLocale();

    i18next
      .use(i18nextHttpBackend)
      .init({
        lng: userLang,
        fallbackLng: 'en_US',
        load: 'currentOnly',
        debug: true,
        defaultNS: 'translation',
        ns: ['translation'],
        supportedLngs: ['en_US', 'es_ES', 'de_DE', 'fr_FR', 'ja_JP', 'it_IT', 'pt_BR'],
        backend: {
          loadPath: iam.settings.localization + '{{lng}}/{{ns}}.json?_=' + iamLocalization.randomInt()
        },
        interpolation: {
          // IAM-5581 - Names containing Apostrophes are not displayed correctly on IAM dashboard:
          // escapeValue now defaulting to false so strings with html characters, like quotes, won't be escaped.
          escapeValue: false
        }
      }, (err, t) => {
        console.log('i18n is initialized and loaded.');
        if (err) {
          console.error('Error in i18n', err);
        } else {
          callback();
          iamLocalization.translate();
        }
      });
  },
  
  localizationFunction: [] as (() => void)[],
  
  addLocalization: (callback: () => void) => { // eslint-disable-line no-unused-vars
    iamLocalization.localizationFunction.push(callback);
  },
  
  translate: async () => {
    let userLang;
    if (document.getElementById('selectLanguage')) {
      userLang = document.getElementById('selectLanguage').dataset.value;
    } else {
      userLang = iamLocalization.getLocale();
    }

    i18next.changeLanguage(userLang, (err, t) => {
      if (err) return console.log('Error loading localization.', err);
      for (let i = 0; i < iamLocalization.localizationFunction.length; i++) {
        iamLocalization.localizationFunction[i]();
      }
    });
    await iamLocalization.loadOktaJSON();
  },
  
  getCookieValue: (cookieName: string) => {
    const cookie = decodeURIComponent(document.cookie).split(';').map(cookie => cookie.trim()).find(one => one.startsWith(`${cookieName}=`));
    return cookie ? cookie.substring(cookieName.length + 1) : null;
  },

  getLocale: () => {
    let userLang = 'en_US'; // default if no match is found below
    const possibleLocales = [...window.navigator.languages.map(lang => lang.replace('-', '_'))];
    const cookieValue = iamLocalization.getCookieValue('iam_user_locale');
    if (cookieValue) {
      possibleLocales.unshift(cookieValue); // prioritize cookie if present
    }
    // try to find a match with just the language
    for (const possibleLocale of possibleLocales) {
      const match = iam.settings.languageOptions.find(lang => possibleLocale.split('_')[0] === lang.langCode.split('_')[0]);
      if (match) {
        userLang = match.langCode;
        break;
      }
    }
    if (iam.settings.debug) console.log('locale is ' + userLang);
    return userLang;
  },
  
  loadOktaJSON: () => {
    const userLang = iamLocalization.getLocale();
    const xmlhttp = new XMLHttpRequest();
    xmlhttp.onreadystatechange = () => {
      if (xmlhttp.readyState === XMLHttpRequest.DONE) {
        if (xmlhttp.status === 200) {
          if (iam.settings.debug) console.log('Data for ' + userLang.split('_')[0], xmlhttp.responseText);
          iam.settings.i18n[userLang.split('_')[0]] = JSON.parse(xmlhttp.responseText);
        } else {
          console.error('Error reading localization file for okta', xmlhttp.status);
        }
      }
    };
    xmlhttp.open('GET', iam.settings.localization + userLang + '/okta.json', true);
    xmlhttp.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    xmlhttp.send(null);
  },
  
  loadLocalization: () => { // eslint-disable-line no-unused-vars
    return new Promise<void>(resolve => {
      if (document.readyState !== 'loading') {
        iamLocalization.loadOktaJSON();
        iamLocalization.loadI18NLibs(() => resolve());
      } else {
        window.addEventListener('DOMContentLoaded', () => {
          iamLocalization.loadOktaJSON();
          iamLocalization.loadI18NLibs(() => resolve());
        });
      }
    });
  }
};

(window as any).iam.Localization = iamLocalization;
